import cx from 'classnames';
import { createContext, forwardRef, HTMLAttributes, useContext } from 'react';
import { Card } from '../Card';
import css from './Table.module.scss';

enum RowParentType {
  BODY = 'BODY',
  FOOT = 'FOOT',
  HEAD = 'HEAD',
}

/**
 * Context
 */
export interface IRowParentContext {
  type: RowParentType;
}

const RowParentContext = createContext<IRowParentContext>({
  type: undefined,
});

const useRowParentContext = () => useContext(RowParentContext);

export interface ITableContainerProps extends HTMLAttributes<HTMLElement> {}

/**
 * Components
 */
const TableContainer = ({ className, ...rest }: ITableContainerProps) => (
  <Card className={cx(css.container, className)} {...rest} />
);

export interface IHeaderProps extends HTMLAttributes<HTMLElement> {
  isSticky?: boolean;
}

const Header = ({ children, className, isSticky }: IHeaderProps) => (
  <header
    className={cx(css.header, className, {
      [css.sticky]: isSticky,
    })}
  >
    {children}
  </header>
);

export interface IFooterProps extends HTMLAttributes<HTMLElement> {}

const Footer = (props: HTMLAttributes<HTMLElement>) => (
  <footer className={css.footer} {...props} />
);

const Title = ({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cx(css.title, className)} {...rest} />
);

interface ITableProps extends HTMLAttributes<HTMLTableElement> {
  isFixed?: boolean;
}

const Table = ({ className, isFixed, ...rest }: ITableProps) => (
  <table
    className={cx(css.table, className, {
      [css.fixed]: isFixed,
    })}
    {...rest}
  />
);

const Head = ({ className, ...rest }: HTMLAttributes<HTMLElement>) => (
  <RowParentContext.Provider value={{ type: RowParentType.HEAD }}>
    <thead className={cx(css.head, className)} {...rest} />
  </RowParentContext.Provider>
);

const Body = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLElement>>(
  ({ className, ...rest }, ref) => (
    <RowParentContext.Provider value={{ type: RowParentType.BODY }}>
      <tbody ref={ref} className={cx(css.body, className)} {...rest} />
    </RowParentContext.Provider>
  )
);

Body.displayName = 'Body';

export interface IRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isActive?: boolean;
  isClickable?: boolean;
  isReset?: boolean;
}

const Row = forwardRef<HTMLTableRowElement, IRowProps>(
  ({ isActive, isClickable = true, isReset, className, ...rest }, ref) => (
    <tr
      ref={ref}
      className={cx(css.row, className, {
        [css.active]: isActive,
        [css.clickable]: isClickable,
        [css.reset]: isReset,
      })}
      {...rest}
    />
  )
);

Row.displayName = 'Row';

interface ICellProps extends HTMLAttributes<HTMLTableCellElement> {
  colSpan?: number;
  isNoWrap?: boolean;
  rowSpan?: number;
  width?: number | string;
}

const Cell = ({ className, isNoWrap, width, ...rest }: ICellProps) => {
  const { type } = useRowParentContext();

  const getComponent = () => {
    switch (type) {
      case RowParentType.BODY:
      case RowParentType.FOOT:
        return 'td';
      case RowParentType.HEAD:
        return 'th';
    }
  };

  const Component = getComponent();

  return (
    <Component
      className={cx(css.cell, className, {
        [css.noWrap]: isNoWrap,
      })}
      style={{ width }}
      {...rest}
    />
  );
};

interface IBooleanProps {
  className?: string;
  value: boolean;
}

const Boolean = ({ className, value }: IBooleanProps) => (
  <div
    className={cx(css.boolean, className, {
      [css.true]: value,
    })}
  />
);

TableContainer.Header = Header;
TableContainer.Title = Title;
TableContainer.Footer = Footer;

Table.Body = Body;
Table.Boolean = Boolean;
Table.Cell = Cell;
Table.Head = Head;
Table.Row = Row;
Table.Table = Table;

export { Table, TableContainer };
